import { useEffect, useState } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEditorialTitle } from '../titleContext'
import { canPublish, useEditorialMe } from '../me'
import { etDate } from '../labels'
import { describeError } from '../../lib/describeError'
import { useDebounced } from '../../lib/useDebounced'
import { SocialCopy } from './AwardPanel'
import { ChallengesTab } from './ChallengesTab'
import {
  RETRY_LABEL, approve, barPct, breakdownLine, checkChip, monthLabel, numbers, classLabel, grantLine, magazinePackageHref, queue, retryAction, retryGrant, rideHref, shortlist, winners,
  type QueueRow, type QueueStatus, type Tone, type WinnerRow,
} from './ridesApi'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const field = `min-h-11 rounded-lg border border-ink-400 bg-ink-800 px-3 font-ui text-[14px] text-paper placeholder:text-paper-faint ${focus}`
const btn = `inline-flex min-h-9 items-center justify-center whitespace-nowrap rounded-lg border border-ink-400 px-3 font-ui text-[13px] font-semibold text-paper hover:bg-ink-600 disabled:opacity-50 ${focus}`
const btnPrimary = `inline-flex min-h-9 items-center justify-center whitespace-nowrap rounded-lg border border-amber-glow bg-amber-glow px-3 font-ui text-[13px] font-semibold text-ink-900 hover:opacity-90 disabled:opacity-50 ${focus}`
const th = 'whitespace-nowrap border-b border-ink-500 px-4 py-3 text-left font-ui text-[12px] font-bold uppercase tracking-[0.06em] text-paper-faint'
const td = 'border-b border-ink-600 px-4 py-3.5 align-middle'

export const TONE: Record<Tone, string> = {
  ok: 'bg-radar-cyan/15 text-radar-cyan',
  warn: 'bg-amber-glow/20 text-amber-deep',
  bad: 'bg-radar-red/15 text-radar-red',
}
export function CheckChips({ checks }: { checks: string[] }) {
  if (checks.length === 0) return <span className="font-ui text-[12px] text-paper-faint">Looks good</span>
  return (
    <span className="flex flex-wrap gap-1">
      {checks.map((c) => {
        const k = checkChip(c)
        return <span key={c} className={`inline-flex h-6 items-center rounded-full px-2.5 font-ui text-[12px] font-semibold ${TONE[k.tone]}`}>{k.label}</span>
      })}
    </span>
  )
}

type Tab = QueueStatus | 'winners' | 'challenges' | 'numbers'
const TABS: { key: Tab; label: string }[] = [
  { key: 'pending', label: 'To review' },
  { key: 'needs_photos', label: 'Waiting for photos' },
  { key: 'live', label: 'Live' },
  { key: 'shortlist', label: 'Shortlist' },
  { key: 'winners', label: 'Winners' },
  { key: 'challenges', label: 'Challenges' },
  { key: 'numbers', label: 'Numbers' },
]
const parseTab = (v: string | null): Tab => TABS.find((t) => t.key === v)?.key ?? 'pending'
const toneText: Record<Tone, string> = { ok: 'text-radar-cyan', warn: 'text-amber-deep', bad: 'text-radar-red' }

function WinnerItem({ row, path, pub, editor }: { row: WinnerRow; path: string; pub: string; editor: boolean }) {
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const retry = useMutation({
    mutationFn: () => retryGrant(pub, row.award_id),
    onSuccess: () => { setError(null); void qc.invalidateQueries({ queryKey: ['rides-winners', pub] }) },
    onError: (e) => setError(describeError(e)),
  })
  const g = grantLine(row.grant)
  const action = retryAction(row.grant_result, row.notified_at !== null)
  return (
    <>
      <tr>
        <td className={`${td} font-ui text-[14px] font-semibold text-paper`}>★ {row.honor}</td>
        <td className={`${td} max-w-[300px]`}>
          <Link to={rideHref(path, row.ride.id)} className={`block font-serif text-[18px] font-medium leading-tight text-paper no-underline hover:underline ${focus}`}>{row.ride.title}</Link>
          <span className="block font-ui text-[12px] text-paper-faint">{row.builder}</span>
        </td>
        <td className={`${td} max-w-[320px] font-ui text-[13px]`}>
          <span className={toneText[g.tone]}>{g.text}</span>
          {editor && action === 'retry' && (
            <button type="button" className={`${btn} ml-2`} disabled={retry.isPending} onClick={() => retry.mutate()}>{retry.isPending ? 'Working…' : RETRY_LABEL.retry}</button>
          )}
          {error && <span role="alert" className="mt-1 block text-[12px] text-radar-red">{error}</span>}
        </td>
        <td className={`${td} whitespace-nowrap font-ui text-[13px] text-paper-dim`}>
          {row.notified_at ? `Emailed ${etDate(row.notified_at)}` : 'Not emailed'}
          {editor && action === 'send' && (
            <button type="button" className={`${btn} ml-2`} disabled={retry.isPending} onClick={() => retry.mutate()}>{retry.isPending ? 'Working…' : RETRY_LABEL.send}</button>
          )}
        </td>
        <td className={`${td} text-right`}>
          <span className="inline-flex flex-wrap justify-end gap-1.5">
            {editor && <a className={btn} href={magazinePackageHref(pub, row.ride.id)} download>Magazine package</a>}
            <button type="button" className={btn} aria-expanded={open} onClick={() => setOpen(!open)}>{open ? 'Hide social posts' : 'Social posts'}</button>
          </span>
        </td>
      </tr>
      {open && (
        <tr><td colSpan={5} className={td}><SocialCopy social={row.social} /></td></tr>
      )}
    </>
  )
}

function Winners({ path, pub, editor }: { path: string; pub: string; editor: boolean }) {
  const q = useQuery({ queryKey: ['rides-winners', pub], queryFn: () => winners(pub) })
  if (q.isPending) return <p role="status" className="m-0 text-[14px] text-paper-dim">Loading…</p>
  if (q.isError) return <p role="alert" className="m-0 text-[14px] text-radar-red">Could not load the winners. {describeError(q.error)}</p>
  return (
    <div className="overflow-x-auto rounded-xl border border-ink-500 bg-ink-700">
      <table className="w-full min-w-[820px] border-collapse">
        <thead>
          <tr>
            <th scope="col" className={th}>Award</th>
            <th scope="col" className={th}>Ride</th>
            <th scope="col" className={th}>Membership</th>
            <th scope="col" className={th}>Email</th>
            <th scope="col" className={th}><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {q.data.rows.map((r) => <WinnerItem key={r.award_id} row={r} path={path} pub={pub} editor={editor} />)}
          {q.data.rows.length === 0 && <tr><td colSpan={5} className={`${td} text-[14px] text-paper-dim`}>No awards yet. Award a live ride from its review page.</td></tr>}
        </tbody>
      </table>
    </div>
  )
}

function Row({ row, path, pub, editor }: { row: QueueRow; path: string; pub: string; editor: boolean }) {
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const done = () => { setError(null); void qc.invalidateQueries({ queryKey: ['rides-queue', pub] }) }
  const appr = useMutation({ mutationFn: () => approve(pub, row.id), onSuccess: done, onError: (e) => setError(describeError(e)) })
  const star = useMutation({ mutationFn: () => shortlist(pub, row.id, !row.shortlisted), onSuccess: done, onError: (e) => setError(describeError(e)) })
  const canApprove = editor && (row.status === 'pending' || row.status === 'needs_photos')
  return (
    <tr>
      <td className={`${td} w-24`}>
        {row.cover_url ? <img src={row.cover_url} alt="" className="h-16 w-24 rounded-md object-cover" /> : <div className="h-16 w-24 rounded-md bg-ink-600" />}
      </td>
      <td className={`${td} max-w-[360px]`}>
        <Link to={rideHref(path, row.id)} className={`block font-serif text-[18px] font-medium leading-tight text-paper no-underline hover:underline ${focus}`}>{row.title}</Link>
        <span className="block font-ui text-[12px] text-paper-faint">
          {classLabel(row.class_slug)}{row.challenge ? ` · ${row.challenge}` : ''}{row.channel ? ` · via ${row.channel}` : ''}
        </span>
        {row.shortlisted && <span className="mt-1 inline-flex h-6 items-center rounded-full bg-amber-glow/20 px-2.5 font-ui text-[12px] font-semibold text-amber-deep">Shortlisted</span>}
        {error && <span role="alert" className="mt-1 block font-ui text-[12px] text-radar-red">{error}</span>}
      </td>
      <td className={`${td} whitespace-nowrap font-ui text-[14px] text-paper-dim`}>{row.submitted_at ? etDate(row.submitted_at) : '—'}</td>
      <td className={`${td} whitespace-nowrap font-ui text-[14px] text-paper-dim`}>
        {row.photo_count} · {row.full_size_count === row.photo_count ? 'all full-size' : row.full_size_count === 0 ? 'all small' : `${row.full_size_count} full-size`}
      </td>
      <td className={td}><CheckChips checks={row.checks} /></td>
      <td className={`${td} text-right`}>
        <span className="inline-flex flex-wrap justify-end gap-1.5">
          {canApprove && <button type="button" className={btnPrimary} disabled={appr.isPending} onClick={() => appr.mutate()}>{appr.isPending ? 'Approving…' : 'Approve'}</button>}
          {editor && row.status !== 'declined' && row.status !== 'merged' && row.status !== 'retired' && (
            <button type="button" className={btn} aria-pressed={row.shortlisted} disabled={star.isPending} onClick={() => star.mutate()}>{row.shortlisted ? 'Remove from shortlist' : 'Shortlist'}</button>
          )}
          <Link to={rideHref(path, row.id)} aria-label={`Review ${row.title}`} className={btn}>Review</Link>
        </span>
      </td>
    </tr>
  )
}

function NumbersTab({ pub }: { pub: string }) {
  const q = useQuery({ queryKey: ['rides-numbers', pub], queryFn: () => numbers(pub) })
  if (q.isPending) return <p role="status" className="m-0 text-[14px] text-paper-dim">Loading…</p>
  if (q.isError) return <p role="alert" className="m-0 text-[14px] text-radar-red">Could not load the numbers. {describeError(q.error)}</p>
  const d = q.data
  const max = Math.max(0, ...d.months.map((m) => m.entries))
  const totals = [
    { label: 'Ride page sessions', value: d.ride_page_sessions },
    { label: 'Newsletter sign-ups from rides', value: d.newsletter_signups_from_rides },
    { label: 'Memberships from Readers Rides', value: d.memberships_from_rides },
  ]
  return (
    <div className="flex flex-col gap-4">
      <p className="m-0 text-[13px] text-paper-faint">The last 12 months, by Eastern calendar month.</p>
      <dl className="m-0 grid gap-3 md:grid-cols-3">
        {totals.map((t) => (
          <div key={t.label} className="rounded-xl border border-ink-500 bg-ink-700 p-4">
            <dt className="font-ui text-[12px] font-bold uppercase tracking-[0.06em] text-paper-faint">{t.label}</dt>
            <dd className="m-0 font-serif text-[32px] font-medium text-paper">{t.value.toLocaleString()}</dd>
          </div>
        ))}
      </dl>
      <div className="overflow-x-auto rounded-xl border border-ink-500 bg-ink-700">
        <table className="w-full min-w-[820px] border-collapse">
          <thead>
            <tr><th className={th}>Month</th><th className={th}>Entries</th><th className={th}>Approved</th><th className={th}>By channel</th><th className={th}>By challenge</th></tr>
          </thead>
          <tbody>
            {[...d.months].reverse().map((m) => (
              <tr key={m.month}>
                <td className={`${td} whitespace-nowrap text-[14px] font-semibold`}>{monthLabel(m.month)}</td>
                <td className={td}>
                  <span className="flex items-center gap-2 text-[14px]">
                    <span className="inline-block h-2 rounded-full bg-amber-glow" style={{ width: `${barPct(m.entries, max) * 0.8}px` }} aria-hidden="true" />
                    {m.entries}
                  </span>
                </td>
                <td className={`${td} text-[14px]`}>{m.approved}</td>
                <td className={`${td} text-[13px] text-paper-dim`}>{breakdownLine(m.by_channel)}</td>
                <td className={`${td} text-[13px] text-paper-dim`}>{breakdownLine(m.by_challenge)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function RidesDeskPage() {
  const { section, publicationSlug } = useEditorialTitle()
  const me = useEditorialMe()
  const editor = canPublish(me.data)
  const [params, setParams] = useSearchParams()
  const tab = parseTab(params.get('tab'))
  const urlQ = params.get('q') ?? ''
  const page = Math.max(1, Number(params.get('page')) || 1)
  const [text, setText] = useState(urlQ)
  const debounced = useDebounced(text, 300)
  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(changes)) { if (v) next.set(k, v); else next.delete(k) }
    setParams(next, { replace: true })
  }
  useEffect(() => {
    if (debounced !== urlQ) update({ q: debounced.trim() ? debounced : null, page: null })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced])

  const isQueue = tab !== 'winners' && tab !== 'challenges' && tab !== 'numbers'
  const q = useQuery({
    queryKey: ['rides-queue', publicationSlug, tab, urlQ, page],
    queryFn: () => queue(publicationSlug, { status: tab as QueueStatus, q: urlQ, page }),
    enabled: section.readers_rides === true && isQueue,
  })
  if (section.readers_rides !== true) return <Navigate to={`/editorial/${section.path}`} replace />
  const total = q.data?.total ?? 0
  const pages = q.data ? Math.max(1, Math.ceil(q.data.total / q.data.page_size)) : 1

  return (
    <div className="flex flex-col gap-5 font-ui text-paper">
      <header className="flex flex-col gap-1.5">
        <span className="label">{section.label} · Readers Rides</span>
        <h1 className="m-0 font-serif text-[40px] font-medium leading-[1.05] tracking-tight text-paper">Rides readers sent in</h1>
        {!editor && me.isSuccess && <p className="m-0 text-[13px] text-paper-faint">You can look at every entry. An editor approves, declines and edits them.</p>}
      </header>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="tablist" aria-label="Queue" className="flex flex-wrap gap-1 rounded-[10px] bg-ink-800 p-1">
          {TABS.map((t) => {
            const on = t.key === tab
            return (
              <button key={t.key} type="button" role="tab" aria-selected={on} onClick={() => update({ tab: t.key === 'pending' ? null : t.key, page: null })}
                className={`min-h-9 rounded-[7px] px-3 font-ui text-[13px] font-semibold ${focus} ${on ? 'bg-ink-600 text-paper' : 'text-paper-dim hover:text-paper'}`}>
                {t.label}{on && q.data && <span className="ml-1.5 font-medium opacity-70">{total}</span>}
              </button>
            )
          })}
        </div>
        {isQueue && (
          <label className="flex">
            <span className="sr-only">Search rides</span>
            <input type="search" value={text} onChange={(e) => setText(e.target.value)} placeholder="Search by ride or builder" className={`${field} w-64`} />
          </label>
        )}
      </div>

      {tab === 'numbers' && <NumbersTab pub={publicationSlug} />}
      {tab === 'challenges' && <ChallengesTab pub={publicationSlug} path={section.path} editor={editor} />}
      {tab === 'winners' && <Winners path={section.path} pub={publicationSlug} editor={editor} />}

      {isQueue && q.isPending && <p role="status" className="m-0 text-[14px] text-paper-dim">Loading…</p>}
      {q.isError && <p role="alert" className="m-0 text-[14px] text-radar-red">Could not load the rides. {describeError(q.error)}</p>}

      {q.data && (
        <div className="overflow-x-auto rounded-xl border border-ink-500 bg-ink-700">
          <table className="w-full min-w-[820px] border-collapse">
            <thead>
              <tr>
                <th scope="col" className={th}><span className="sr-only">Photo</span></th>
                <th scope="col" className={th}>Ride</th>
                <th scope="col" className={th}>Entered</th>
                <th scope="col" className={th}>Photos</th>
                <th scope="col" className={th}>Checks</th>
                <th scope="col" className={th}><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {q.data.rows.map((r) => <Row key={r.id} row={r} path={section.path} pub={publicationSlug} editor={editor} />)}
              {q.data.rows.length === 0 && <tr><td colSpan={6} className={`${td} text-[14px] text-paper-dim`}>Nothing here.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {isQueue && pages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button type="button" className={btn} disabled={page <= 1} onClick={() => update({ page: page - 1 > 1 ? String(page - 1) : null })}>Previous</button>
          <span className="text-[13px] text-paper-dim">Page {page} of {pages}</span>
          <button type="button" className={btn} disabled={page >= pages} onClick={() => update({ page: String(page + 1) })}>Next</button>
        </div>
      )}
    </div>
  )
}
